import { getTimeframeSeconds } from '../../utils/chartUtils'
import type { CandleData, OrderData } from '../../bot/types/chartData'
import { closesByKey, pairKeyOf } from '../../bot/utils/orderPairing'
import { isTradeMark } from './tradeIdeas'
import { roleOfSlot } from './tradeIdeaVocab'
import type { SavedDrawing } from './workbenchState'
import type { WalkthroughSpan } from '../../bot/api'

/**
 * Bars of chart the user sees before the first drawing.
 *
 * Two, and deliberately not more. The RUN reaches much further back — the server pads it with
 * hundreds of bars of indicator warm-up, sized from the strategy's longest lookback — because an
 * SMA(200) needs 200 bars before it can trade. None of that is the user's business in a
 * walkthrough: it is "does the code do what I drew?", and everything before the first drawing is
 * machinery. Nor are the orders the code placed during it (see `displaySpan`). Two bars is just
 * enough for the first marker not to sit on the chart's first candle.
 *
 * One exception, which moves the start rather than this count: see PAIR_WINDOW_BARS.
 */
export const VISIBLE_LEAD_BARS = 2

/**
 * Bars either side of a drawn entry within which the verification pairs a trade with it.
 *
 * Mirrors the server's WalkthroughReport.CLOSE_BARS, and has to: a strategy confirms on a candle
 * close or waits out a filter, so the code routinely enters a few bars off where the user
 * clicked — and the Auto-verify loop accepts that as "close". A chart that started two bars
 * before the marker would then hide the very trade the verification accepted, leaving the user
 * looking at a marker with nothing near it. So a trade that pairs with a drawn entry pulls the
 * start back to itself (see displaySpan); the rest of the warm-up stays hidden.
 */
export const PAIR_WINDOW_BARS = 24

/**
 * Bars of chart held after the last drawing, and after the last trade the window has to show.
 *
 * Only a margin: what actually decides how far the chart runs past the drawings is
 * MIN_VISIBLE_TRADES. This keeps the last thing worth seeing off the chart's final candle.
 */
export const VISIBLE_TRAIL_BARS = 5

/**
 * Trades the chart runs on for, when the drawings alone would show fewer.
 *
 * One drawn idea, one trade, and a replay that stops there says very little about the code. So
 * the visible window keeps going until this many trades have entered AND closed — never past the
 * run's own end, which is the hard limit (the server's TRAIL_BARS), and never cutting a drawing
 * or the exit of a trade taken while the drawings were still running.
 */
export const MIN_VISIBLE_TRADES = 3

/**
 * The window the user sees — as opposed to the run's real range.
 *
 * The server's range is padded with indicator warm-up before the drawings and ~200 bars of
 * trail after them, and rounded out to whole UTC days at both ends. That is right for the
 * engine and wrong for the chart: it is the bigger of the two windows, and this is the smaller.
 * The chart loads, reveals and pans within this one only, the transport's 0% and 100% are its
 * two ends, and no trade entered before its start is listed or drawn.
 */
export interface DisplaySpan {
  /** unix seconds. The chart's left bound, where the replay opens and Restart lands. */
  startTs: number
  /** unix seconds. The chart's right bound, and the transport's 100%. */
  endTs: number
}

/**
 * The visible window: from just before the first drawing to where enough has been shown.
 *
 * START. `VISIBLE_LEAD_BARS` before the earlier of the first drawing and the entry of any trade
 * the verification pairs with a drawn entry (see PAIR_WINDOW_BARS — a trade the code took five
 * bars before the marker is the marker's trade, not warm-up). Counted on `candles` when there are
 * some, so a drawing on a Monday morning starts on Friday's last bars rather than on an empty
 * weekend; otherwise the same count of bar lengths back in wall-clock time.
 *
 * END. The latest of:
 *  - the last drawing, so every drawing is on the chart;
 *  - the exit of every trade entered by then, so a trade the drawings led into is seen through;
 *  - the exit of the `MIN_VISIBLE_TRADES`-th trade, so a single drawn idea still shows a few;
 * each plus `VISIBLE_TRAIL_BARS`. When the whole run holds fewer trades than that past the start,
 * the end is the run's own — it is as far as there is to look.
 *
 * Only trades ENTERED at or after the start count, here and on the chart and in the trades pane
 * alike: a position the code took during the warm-up belongs to the machinery, not to the user's
 * drawings, even if it is still open when they begin.
 *
 * Clamped into the run's real range, which is the hard limit on both sides.
 */
export function displaySpan(
  span: DrawingSpan,
  timeframe: string,
  rangeStartTs: number,
  rangeEndTs: number,
  { orders = [], entries = [], candles = [] }: DisplaySpanInputs = {},
): DisplaySpan {
  const tfSec = getTimeframeSeconds(timeframe)
  const clamp = (ts: number) => Math.max(rangeStartTs, Math.min(rangeEndTs, ts))
  const firstDrawingSec = Math.floor(span.fromTs / 1000)
  const lastDrawingSec = Math.floor(span.toTs / 1000)
  const pad = VISIBLE_TRAIL_BARS * tfSec

  const anchorTs = Math.min(firstDrawingSec, ...pairedEntryTimes(entries, orders, tfSec))
  const startTs = clamp(barBefore(candles, anchorTs, VISIBLE_LEAD_BARS) ?? anchorTs - VISIBLE_LEAD_BARS * tfSec)

  const trades = visibleTrades(orders, startTs)
  if (trades.length < MIN_VISIBLE_TRADES) return { startTs, endTs: rangeEndTs }

  const drawnEnd = lastDrawingSec + pad
  let endTs = drawnEnd
  trades.forEach((t, i) => {
    if (i >= MIN_VISIBLE_TRADES && t.entryTs > drawnEnd) return
    // A trade still open at the end of the run has no exit to show; its entry is the last
    // thing about it the chart can hold.
    endTs = Math.max(endTs, (t.exitTs ?? t.entryTs) + pad)
  })
  return { startTs, endTs: clamp(endTs) }
}

export interface DisplaySpanInputs {
  /** Every order event of the run, unix seconds — both halves of each trade. */
  orders?: OrderData[]
  /** The drawn entries, for the trades the verification pairs with them (see drawnEntries). */
  entries?: DrawnEntry[]
  /** Candles up to the first drawing, oldest first, to count the lead in real bars. */
  candles?: CandleData[]
}

/** A drawn entry: where the user said a trade should open, and which way. */
export interface DrawnEntry {
  /** unix seconds */
  ts: number
  side: 'BUY' | 'SELL'
}

/**
 * The entries drawn on the chart — the trade-idea tool's entry marks, and the retired buy/sell
 * entry markers — exactly the set the server's report pairs trades against (WorkbenchState
 * .tradeIdeas). Locked drawings and connector chrome are not user intent, there as here.
 */
export function drawnEntries(drawings: SavedDrawing[]): DrawnEntry[] {
  const out: DrawnEntry[] = []
  for (const d of drawings) {
    if (d.lock || d.extendData?.connector === true) continue
    const ts = d.points?.[0]?.timestamp
    if (typeof ts !== 'number' || !Number.isFinite(ts)) continue
    const ed = d.extendData ?? {}
    let side: DrawnEntry['side'] | null = null
    if (isTradeMark(d.name)) {
      if (roleOfSlot(typeof ed.slot === 'string' ? ed.slot : undefined) === 'entry') {
        side = ed.dir === 'sell' ? 'SELL' : 'BUY'
      }
    } else if (d.name === 'buyEntry' || d.name === 'sellEntry') {
      side = d.name === 'sellEntry' ? 'SELL' : 'BUY'
    }
    if (side) out.push({ ts: Math.floor(ts / 1000), side })
  }
  return out
}

/**
 * For each drawn entry, the entry time of the same-side trade nearest to it within
 * PAIR_WINDOW_BARS, if any. The report's own pairing, minus its bookkeeping of which trade each
 * idea claimed first — this only ever widens the window, so a looser match costs at most a bar
 * or two of chart, never a hidden trade.
 */
function pairedEntryTimes(entries: DrawnEntry[], orders: OrderData[], tfSec: number): number[] {
  const opens = orders.filter((o) => o.eventType === 'opened')
  const paired: number[] = []
  for (const e of entries) {
    let best: { bars: number; time: number } | null = null
    for (const o of opens) {
      if (o.type !== e.side) continue
      const bars = Math.abs(Math.round((o.time - e.ts) / tfSec))
      if (bars > PAIR_WINDOW_BARS) continue
      if (!best || bars < best.bars) best = { bars, time: o.time }
    }
    if (best) paired.push(best.time)
  }
  return paired
}

/** Open time of the bar `n` bars before the one containing `ts`, or null with no candle there. */
function barBefore(candles: CandleData[], ts: number, n: number): number | null {
  let idx = -1
  for (let i = candles.length - 1; i >= 0; i--) {
    if (candles[i].time <= ts) {
      idx = i
      break
    }
  }
  return idx < 0 ? null : candles[Math.max(0, idx - n)].time
}

/** Trades entered at or after `fromTs`, in entry order, with their exit if the run closed them. */
function visibleTrades(
  orders: OrderData[],
  fromTs: number,
): Array<{ entryTs: number; exitTs: number | null }> {
  const closes = closesByKey(orders)
  return orders
    .filter((o) => o.eventType === 'opened' && o.time >= fromTs)
    .map((o) => {
      const key = pairKeyOf(o)
      return { entryTs: o.time, exitTs: (key == null ? undefined : closes.get(key))?.time ?? null }
    })
    .sort((a, b) => a.entryTs - b.entryTs)
}

/**
 * The raw drawing span the server needs, in MILLISECONDS.
 *
 * Timestamps live on SavedDrawing.points[].timestamp (klinecharts native ms). Points with no
 * timestamp are price-only anchors — a horizontal level line has a price but no position on
 * the time axis — and contribute nothing to the span.
 */
export interface DrawingSpan {
  /** Earliest drawn point, ms. */
  fromTs: number
  /** Latest drawn point, ms. */
  toTs: number
  /** How many drawings contributed a timestamp. */
  count: number
}

/**
 * Collapse the drawings to their time span.
 *
 * Returns null when nothing is anchored in time, which is a real case (a chart with only
 * horizontal levels) and the reason the Walkthrough guard cannot just count drawings.
 */
export function drawingSpan(drawings: SavedDrawing[]): DrawingSpan | null {
  let fromTs = Infinity
  let toTs = -Infinity
  let count = 0

  for (const d of drawings) {
    let contributed = false
    for (const p of d.points ?? []) {
      const ts = p?.timestamp
      if (typeof ts !== 'number' || !Number.isFinite(ts)) continue
      if (ts < fromTs) fromTs = ts
      if (ts > toTs) toTs = ts
      contributed = true
    }
    if (contributed) count += 1
  }

  if (count === 0) return null
  return { fromTs, toTs, count }
}

/** Build the request body the walkthrough endpoints take, or null if there is no span. */
export function walkthroughSpan(
  symbol: string,
  timeframe: string,
  drawings: SavedDrawing[],
): WalkthroughSpan | null {
  const span = drawingSpan(drawings)
  if (!span) return null
  return { symbol, timeframe, fromTs: span.fromTs, toTs: span.toTs }
}
